/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { CompletePurchaseRouteConnectProps } from 'routes/business/CompletePurchaseRoute';
import { DayOfWeek, LocalDateTime } from 'js-joda';
import { FlowRouteComponentProps } from 'components/render-prop-components/FlowRoute';
import { GiftCard } from 'state/giftCard';
import { GiftCardBalanceWrapper } from 'components/business/checkout/GiftCardBalanceWrapper';
import { Head } from 'head/Head';
import {
  IWithContextProps,
  consumer,
  core,
  requestToJSON,
  safeEnumCompare,
  safeToNumber,
  toJSONArray,
} from '@tock/coretypes';
import { IWithNewCardPaymentElementProps, withNewCardPaymentElement } from '@tock/payment-elements';
import { Fields as LoyaltyProgramLoginFormFields } from 'forms/loyalty-program/LoyaltyProgramLoginForm';
import { Fields as LoyaltyProgramRedemptionFormFields } from 'forms/loyalty-program/LoyaltyProgramRedemptionForm';
import { PaymentMethodDetails } from 'utils/Analytics';
import { RouteComponentProps } from 'react-router-dom';
import { SCA_FAILED_ERROR_CODE, exposedErrorCodes, paymentErrorCodes } from '@tock/error-codes';
import { StripePaymentIntentResponse } from '@tock/shared/actions/PaymentCardActions';
import { TRY_ANOTHER_METHOD_ERROR_MESSAGE, convertToApplePayProcessingType } from 'utils/applePay';
import { Theme, css } from '@emotion/react';
import { TokenRefreshHandle } from 'forms/TurnstileValidator';
import { Typography } from 'omakase';
import { addCvvToken } from '@tock/shared/utils/creditCards';
import {
  addDeliveryInfo,
  addPatron,
  addPaymentCardOrCards,
  buildPurchaseOrder,
  getRequiredAge,
  hasAnyMoniesInvolved,
} from 'utils/purchase';
import { addPaymentIntent } from '@tock/shared/utils/paymentIntent';
import { asyncValidateCreditCardForm } from '@tock/shared/forms/credit-card/CreditCardForm';
import { currencyCodeToSymbol, displayInDollars } from '@tock/money';
import { displayRefundInSyncError } from 'components/business/checkout/RefundErrorUtil';
import {
  doorDashMaxTipExceededErrorString,
  isGratuityViolatingDoordashLimit,
} from 'components/business/gratuity/GratuityInput';
import {
  extractDiscounts,
  extractSubtotal,
  extractTotalPrice,
  isCreditCardCollectingPurchase,
  isTotallyFreePurchase,
} from 'utils/purchaseOrderUtils';
import {
  filter,
  find,
  first,
  head,
  includes,
  isEmpty,
  isEqual,
  isNil,
  isUndefined,
  map,
  min,
  some,
  toNumber,
  trim,
} from 'es-toolkit/compat';
import { getAmountDue, getHoldAmountDue } from '@tock/shared/utils/purchase';
import { getDiner } from '@tock/shared/utils/dinerUtils';
import { getEffectivePurchase } from 'components/business/checkout/CompletePurchaseUtils';
import { isBraintreeBusiness } from 'utils/businessUtils';
import { isLockOfEvent } from 'utils/lock';
import { lib } from 'types';
import { refreshCalendar } from 'utils/refreshData';
import { scrollToChild } from '@tock/shared/utils/scroll';
import { shouldUsePaidQuickCheckout } from 'utils/quickCheckoutUtils';
import { stringify } from '@tock/query-string';
import AcceptTermsForm, {
  AcceptTermsFormFields,
  DEFAULT_OPT_IN_TEXT,
  DEFAULT_OPT_IN_TEXT_CASL,
  getPaymentDetails,
  hasRequiredApplePayFields,
} from 'forms/accept-terms/AcceptTermsForm';
import Anchor from 'components/Anchor';
import BaseComponent from '@tock/shared/components/BaseComponent';
import DoubleBookingWarning, {
  DoubleBooking,
} from 'components/business/checkout/DoubleBookingWarning';
import GiftCardWrapper from 'components/business/checkout/GiftCardWrapper';
import LoyaltyProgramModal from 'components/loyalty-program/LoyaltyProgramModal';
import PaymentScriptLoader from '@tock/shared/components/render-prop-components/PaymentScriptLoader';
import PurchaseOrderSummary from 'components/business/receipts/PurchaseOrderSummary';
import RewardsAndDiscountsForm from 'forms/RewardsAndDiscountsForm';
import Section from 'components/Section';
import TicketTypeVarietySpecificInstructions from 'components/business/checkout/TicketTypeVarietySpecificInstructions';
import protos from 'utils/protos';

const { Business, Discount, LoyaltyProgramConsumerTheme } = protos;

interface State {
  isGiftCardShown: boolean;
  isInitialPriceCalculated: boolean;
  orderNotes: string;
  useGiftCardBalance: boolean; // TODO: default to false if in chase, don't show if in chase
  turnstileResponse: string;
}

interface ExternalProps {
  business?: consumer.IBusinessConfig;
  doubleBooking: DoubleBooking;
  isInQuickCheckout?: boolean;
  isOwnerSameAsDiner: boolean;
  isPatronAPartner?: boolean;
  onReschedule?(purchaseOrder: core.IPurchaseOrder): Promise<any>;
  onSuccess(purchase: core.PurchaseOrder): void;
  basePurchase?: core.PastPurchase;
  selectedExperience: core.TicketType;
  stripePublicKey: string;
  shouldAskForSmsConsent: boolean;
}

export type CompletePurchaseProps = CompletePurchaseRouteConnectProps &
  IWithContextProps &
  IWithNewCardPaymentElementProps &
  FlowRouteComponentProps &
  RouteComponentProps<any> &
  ExternalProps;

interface saveCardToTockOptions {
  patron?: core.IPatron | undefined;
  scope: ApiScope | undefined;
  createWithoutPatron: boolean;
  challengeResponse?: string;
  requireTurnstile?: boolean;
}

class CompletePurchase extends BaseComponent<CompletePurchaseProps, State> {
  turnstileValidatorRef: React.RefObject<TokenRefreshHandle>;

  constructor(props: CompletePurchaseProps) {
    super(props);

    this.state = {
      isGiftCardShown: !isEmpty(props?.giftCard.giftCards || []),
      isInitialPriceCalculated: false,
      orderNotes: '',
      useGiftCardBalance: false,
      turnstileResponse: '',
    };
    this.turnstileValidatorRef = React.createRef();
  }

  componentDidMount() {
    const {
      app: { config },
      lock,
      purchase,
      basePurchase,
      selection,
      giftCard,
    } = this.props;
    const business = config?.business;
    if (isEmpty(giftCard.giftCards)) {
      this.handleInitializeGiftCards();
    }
    const loyaltyProgramTheme = business?.loyaltyProgramTheme;
    if (
      safeEnumCompare(
        loyaltyProgramTheme,
        LoyaltyProgramConsumerTheme,
        LoyaltyProgramConsumerTheme.ANNEX_CLOUD_DEFAULT
      )
    ) {
      this.handleInitializeAnnexCloudLoyaltyProgram();
    }

    setTimeout(() => {
      // On page refresh, reset the gratuity to the middle option if falsey. ie. set to 0.
      const selectedGratuity = purchase.calculatedPrice?.selectedGratuityRate ?? undefined;
      const optionTwo =
        lock.currentLock?.ticketType?.[0]?.extraChargeDetails?.gratuityOptionTwo ?? undefined;
      const rescheduleGratuityRate = !isNil(selectedGratuity)
        ? selectedGratuity
        : !isNil(optionTwo)
          ? optionTwo
          : undefined;
      const newPurchaseGratuityRate =
        purchase.calculatedPrice?.selectedGratuityRate ||
        lock.currentLock?.ticketType?.[0]?.extraChargeDetails?.gratuityOptionTwo ||
        undefined;
      this.handleUpdatePrice({
        newGratuityRate: basePurchase ? rescheduleGratuityRate : newPurchaseGratuityRate,
      }).then(() => {
        this.initializeTockGiftCards();
      });
      if (this.getIsCreditCardCollectingPurchase()) {
        this.fetchCardInfo();
      }
      window.document.addEventListener('visibilitychange', this.handleDocumentVisible);
      let subheading = '';
      if (!this.props.isOwnerSameAsDiner && !selection.visitEditToken) {
        subheading += `As the reservation owner, you'll receive all email communication and retain control of the reservation including the ability to transfer.`;
      }
      this.props.actions.checkout.setCheckoutStatus(
        'Review and complete your reservation',
        subheading
      );
    }, 0);
  }

  componentDidUpdate(prevProps: CompletePurchaseProps) {
    const { purchase, giftCard } = this.props;

    if (!prevProps.purchase.paymentError && purchase.paymentError) {
      // this.scrollToPaymentCard();
    }

    if (
      this.getIsCreditCardCollectingPurchase() &&
      !this.getIsCreditCardCollectingPurchase(prevProps)
    ) {
      this.fetchCardInfo();
    }

    // Check if the gift card props have changed and if so, fire a price update
    const prevGiftCards = prevProps.giftCard.giftCards;
    const newGiftCards = giftCard.giftCards;

    if (!isEqual(prevGiftCards, newGiftCards)) {
      this.handleUpdatePrice({});
    }

    if (
      (!prevProps.giftCard.tockGiftCard && giftCard.tockGiftCard) ||
      (prevProps.giftCard.tockGiftCard && !giftCard.tockGiftCard)
    ) {
      this.handleUpdatePrice({});
    }
  }

  /**
   * clear out gift cards, so if the user goes back to edit the
   * order, they have to re-apply it
   */
  componentWillUnmount() {
    const { actions } = this.props;
    actions.giftCard.removeTockGiftCard();
    window.document.removeEventListener('visibilitychange', this.handleDocumentVisible);
  }

  async initializeTockGiftCards() {
    const {
      app,
      actions,
      purchase,
      isOwnerSameAsDiner,
      context: { isFeatureEnabled },
    } = this.props;
    const business = app.config?.business;

    if (
      !business ||
      !isOwnerSameAsDiner ||
      !app.jwtToken ||
      isFeatureEnabled('GOODBYE_TOCK_GIFT_CARDS')
    ) {
      return;
    }

    if (purchase.calculatedPrice) {
      if (
        !purchase.calculatedPrice.isTockGiftCardEligible ||
        purchase.calculatedPrice.totalPrice < 0
      ) {
        return;
      }
      if (
        isBraintreeBusiness(business as consumer.BusinessConfig) &&
        purchase.calculatedPrice.tockGiftCard.length > 0
      ) {
        this.setState((prevState: State) => ({
          useGiftCardBalance: true,
        }));
      }
    }
    const giftCardBalance = await actions.tockGiftCard.checkTockGiftCardBalance();
    this.setState(
      (prevState: State) => ({
        useGiftCardBalance:
          (purchase.calculatedPrice?.isTockGiftCardEligible || false) &&
          (giftCardBalance.payload?.balanceCents ?? 0) > 0 &&
          (purchase.calculatedPrice?.totalPrice ?? 0) > 0 &&
          isBraintreeBusiness(business),
      }),
      () => {
        this.updateGiftCardState(this.state.useGiftCardBalance);
      }
    );
  }

  getStripeObjectForBusiness() {
    const appConfig = this.props.app.config;
    const businessConfig = this.getBusinessConfig();
    if (!appConfig?.stripeApiKey) {
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return window.Stripe!(appConfig?.stripeApiKey, {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      stripeAccount: businessConfig!.paymentClientId!,
    });
  }

  getIsCreditCardCollectingPurchase(passedProps?: CompletePurchaseProps): boolean {
    const purchase = getEffectivePurchase(passedProps, this.props);
    return purchase ? isCreditCardCollectingPurchase(purchase) : false;
  }

  getIsTotallyFreeReservation(passedProps?: CompletePurchaseProps): boolean {
    const purchase = getEffectivePurchase(passedProps, this.props);
    return purchase ? isTotallyFreePurchase(purchase) : false;
  }

  getTotalPrice(passedProps?: CompletePurchaseProps): number {
    const purchase = getEffectivePurchase(passedProps, this.props);
    return purchase ? extractTotalPrice(purchase) : 0;
  }

  getSubtotal(passedProps?: CompletePurchaseProps): number {
    const purchase = getEffectivePurchase(passedProps, this.props);
    return purchase ? extractSubtotal(purchase) : 0;
  }

  getDiscounts(): number {
    const {
      purchase: { calculatedPrice },
    } = this.props;
    return calculatedPrice ? extractDiscounts(calculatedPrice) : 0;
  }

  // Do not save a credit card to a patron's account if the purchase is being
  // booked on behalf of someone else.
  getShouldSaveCreditCard() {
    if (this.props.purchase?.calculatedPrice?.visitEditToken) {
      return true;
    }
    return this.props.isOwnerSameAsDiner;
  }

  handleTurnstileResponse(turnstileResponse: string | undefined) {
    if (turnstileResponse) {
      this.setState({ turnstileResponse: turnstileResponse });
    }
  }

  fetchCardInfo() {
    const {
      app: { jwtToken },
      actions,
      selection,
      isOwnerSameAsDiner,
    } = this.props;
    const isAuthenticated = !!jwtToken;

    if (isAuthenticated) {
      actions.paymentCard.fetchAll(this.getBusinessScope()).then((paymentCardsProto) => {
        if (!paymentCardsProto.payload) {
          actions.selection.selectCard(null);
          return;
        }
        const paymentCards = toJSONArray<core.PaymentCard>(paymentCardsProto.payload);
        if (paymentCards.length > 0 && (isOwnerSameAsDiner || !!selection.visitEditToken)) {
          if (!selection.card) {
            this.handleSelectCard(paymentCards[0]);
          }
        } else {
          actions.selection.selectCard(null);
        }
      });
    }
  }

  handleAddGiftCard(giftCardToAdd: core.GiftCard, amountCents: number) {
    const { giftCard, actions, context } = this.props;
    const { externalIdentifier: giftCardNumber, giftCardProvider } = giftCardToAdd;
    if (some(giftCard.giftCards, (giftCard) => giftCard.number === giftCardNumber)) {
      actions.toast.showError('This gift card has already been added');
      return;
    }
    const giftCardToApply = {
      number: giftCardNumber,
      amountCents,
      pin: giftCardToAdd.externalPin,
      provider: giftCardProvider,
    };

    actions.giftCard
      .getGiftCardBalance(giftCardToApply)
      .then((action: any) => {
        const availableBalance = action.payload;
        if (availableBalance < 1) {
          actions.toast.showError('The entered gift card has no money left');
          return;
        } else if (availableBalance < amountCents) {
          const currency = context.currency;
          actions.toast.showError(
            `The entered gift card only has ${currencyCodeToSymbol(currency)}${displayInDollars(
              availableBalance
            )} left.`
          );
          return;
        } else {
          actions.giftCard.applyGiftCard(giftCardToApply);
        }
      })
      .catch(() => {
        actions.toast.showError('Unable to apply gift card');
      });
  }

  buildGiftCardDiscounts(): core.IDiscount[] {
    const { basePurchase, purchase, giftCard, loyaltyProgram } = this.props;
    const business = this.getBusinessConfig();

    const totalPrice = this.getTotalPrice();
    let discountedTotalPrice = totalPrice;

    // first calculation
    if (!purchase.calculatedPrice && basePurchase && (basePurchase?.discount.length ?? 0) > 0) {
      return basePurchase.discount;
    } else {
      const giftCardDiscounts = filter(
        map(giftCard.giftCards, (giftCard) => {
          const existingDiscount = find(
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            purchase.calculatedPrice!.discount,
            (discount) =>
              !!discount.giftCard && discount.giftCard.externalIdentifier === giftCard.number
          );
          if (existingDiscount) {
            return existingDiscount;
          }

          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          const amountToDiscount = min([giftCard.amountCents, discountedTotalPrice])!;
          discountedTotalPrice -= amountToDiscount;

          return {
            description: `Gift card ending in ${giftCard.number.substr(-4)}`,
            amountCents: amountToDiscount,
            discountType: Discount.DiscountType.CREDIT,
            giftCard: {
              giftCardProvider: business?.giftCardProvider,
              externalIdentifier: giftCard.number,
              externalPin: giftCard.pin,
            },
          };
        })
      );

      const numPointsToReedeem = loyaltyProgram.numPointsToRedeem || 0;
      if (numPointsToReedeem > 0) {
        giftCardDiscounts.push({
          description: 'Reward Dollars',
          amountCents: numPointsToReedeem * 100,
          discountType: Discount.DiscountType.SUBTOTAL_COMP,
          loyaltyProgram: {
            providerType: loyaltyProgram.balance?.providerType,
          },
        });
      }
      return giftCardDiscounts;
    }
  }

  handleError(response: { error: lib.ErrorMessageProto }) {
    const { actions, app, match, selection, history, newCardPaymentElementData } = this.props;
    const isAuthenticated = !!app.jwtToken;
    if (!response?.error?.errorCode) {
      actions.toast.showError('There was a problem completing your purchase.');
      return;
    }
    const errorCode = safeToNumber(response.error.errorCode);
    const regex = /^.+\.$/i;
    if (includes([3000, 3002, 5007], errorCode)) {
      // Tickets already purchased, locked by someone else, or account locked out
      refreshCalendar(actions, this.props?.calendar?.ticketTypeAccessToken).then(() => {
        history.push(`/${match.params.businessDomain}`);
      });
    } else if (includes(paymentErrorCodes, errorCode)) {
      const errorMessage = regex.test(response.error.message)
        ? response.error.message
        : `${response.error.message}.`;

      if (includes([4023, 9009], errorCode)) {
        actions.purchase.showPaymentError(
          `${errorMessage} Please choose or enter a different card.`
        );
        // Card was declined because of a payment processing error
        // Or used non amex card for exclusive amex purchase.
        // Require the user to re select another card.
        actions.selection.selectCard(null);
      } else if (errorCode === 4018 && window.braintreeHostedField) {
        // Re-validating the payment card has failed.
        actions.toast.showError('The CVV is invalid for this card.');
      } else if (selection.card) {
        actions.purchase.showPaymentError(`${errorMessage} Please re-enter card information.`);
        // Details of the card have been declined (cvc, expiration date)
        actions.toast.showError(response.error.message);
        actions.selection.selectCard(null);
        if (isAuthenticated) {
          actions.paymentCard.fetchAll();
        }
      } else {
        actions.purchase.showPaymentError(
          `${errorMessage} Please choose or enter a different card`
        );
      }
      newCardPaymentElementData.reset();
      return Promise.reject(response.error.message);
    } else if (includes(exposedErrorCodes, errorCode)) {
      // Purchase link has expired,
      // or the purchase has been edited and cannot be transferred,
      // or the delivery address is outside the business's range,
      // or this is an exclusive experience and the wrong card type was used,
      // or loyalty program reward redemption failed.
      // or SCA failed authentication
      this.props.actions.toast.showError(response.error.message);
      const domainName = app.config?.business?.domainName;
      if (errorCode === 3053 && !!selection.visitEditToken) {
        if (selection.visitEditPaymentMode) {
          history.replace(
            `/${domainName}/tableside/payment/?token=${encodeURIComponent(
              selection.visitEditToken
            )}`
          );
        } else {
          history.replace(
            `/${domainName}/tableside/?token=${encodeURIComponent(selection.visitEditToken)}`
          );
        }
      } else if (errorCode === 3065 && !!selection.visitEditToken) {
        // Purchase has already happened and there is no longer need for payment.
        history.replace(
          `/${domainName}/tableside/?token=${encodeURIComponent(selection.visitEditToken)}`
        );
      }
    } else if (errorCode === 2042) {
      this.props.actions.toast.showError('Please select an add-on.');
    } else if (errorCode === 9013) {
      displayRefundInSyncError(this.props.actions.toast);
    } else {
      this.props.actions.toast.showError('There was a problem completing your purchase.');
    }

    return Promise.reject();
  }

  usesPaidQuickCheckout(passedProps?: CompletePurchaseProps) {
    const {
      app: { jwtToken },
      context: { isFeatureEnabled },
    } = this.props;

    const isPaidQuickCheckoutEnabled = isFeatureEnabled('PAID_QUICK_CHECKOUT');
    const purchase = getEffectivePurchase(passedProps, this.props);
    return purchase
      ? shouldUsePaidQuickCheckout(purchase, jwtToken, isPaidQuickCheckoutEnabled)
      : false;
  }

  handleStartPurchase(
    cardToCharge: core.IPaymentCard[] | core.IPaymentCard,
    patron: core.IPatron,
    options: {
      optsInToEmail: boolean;
      hasVerifiedAge: boolean;
      consentsToSMS: boolean;
      optionalOrderNote: string;
      paymentMethodDetails?: PaymentMethodDetails;
    }
  ) {
    const {
      purchase,
      paymentCard,
      selectedExperience,
      delivery,
      loyaltyProgram,
      actions,
      basePurchase,
      onReschedule,
    } = this.props;
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const purchaseOrder: core.PurchaseOrder = { ...purchase.calculatedPrice! };
    const { deviceData } = paymentCard;
    const requiresPayment = this.getRequiresPayment(purchaseOrder);
    const usePaidQuickCheckout = this.usesPaidQuickCheckout();
    if (requiresPayment && cardToCharge) {
      addPaymentCardOrCards(purchaseOrder, cardToCharge, deviceData);
    } else {
      addPatron(purchaseOrder, patron);
    }

    if (selectedExperience.variety === 'ORDER_DELIVERY' && delivery.deliveryAddress) {
      addDeliveryInfo(purchaseOrder, delivery.deliveryAddress, patron);
    }

    if (this.shouldAskOptInQuestion()) {
      purchaseOrder.emailOptIn = !!options.optsInToEmail;
    }
    const requiredAge = this.getRequiredAge();
    if (requiredAge && options.hasVerifiedAge) {
      purchaseOrder.verifiedAge = requiredAge;
    }

    const notes = [];
    if (options.optionalOrderNote) {
      notes.push({
        noteType: 'GUEST_PROVIDED',
        note: options.optionalOrderNote,
      });
    }

    purchaseOrder.consentsToSms = options.consentsToSMS;
    purchaseOrder.bookingNote = notes;
    purchaseOrder.loyaltyProgramUniqueCheckNumber = loyaltyProgram?.uniqueCheckNumber || '';
    if (usePaidQuickCheckout) {
      purchaseOrder.patron = patron;
    }

    let purchasePromise;
    if (window.stripeV3 && purchaseOrder.totalPrice > 0) {
      purchasePromise = actions.paymentCard
        .getPotentialPrePurchaseAction(purchaseOrder, this.getBusinessScope())
        .then(this.handlePrePurchaseAction)
        .then((paymentIntent?: string) => {
          if (basePurchase && onReschedule) {
            return onReschedule(addPaymentIntent(purchaseOrder, paymentIntent));
          }

          return actions.purchase.purchase(
            addPaymentIntent(purchaseOrder, paymentIntent),
            this.getBusinessScope(),
            selectedExperience,
            options.paymentMethodDetails
          );
        });
    } else {
      if (basePurchase && onReschedule) {
        purchasePromise = onReschedule(purchaseOrder);
      } else {
        purchasePromise = actions.purchase.purchase(
          purchaseOrder,
          this.getBusinessScope(),
          selectedExperience
        );
      }
    }

    return requestToJSON<core.PurchaseOrder>(purchasePromise)
      .then(this.props.onSuccess)
      .catch(this.handleError);
  }

  handlePrePurchaseAction(response: any) {
    const nextAction = response?.payload?.scaAction?.stripeAction;
    const paymentMethod = response?.payload?.scaAction?.paymentMethodId;
    const paymentIntent = response?.payload?.scaAction?.paymentIntentId;
    if (nextAction && paymentMethod) {
      return (this.getStripeObjectForBusiness() as any)

        .handleCardPayment(nextAction, { payment_method: paymentMethod })
        .then((intentResponse: StripePaymentIntentResponse) => {
          return intentResponse.error
            ? Promise.reject({
                error: {
                  errorCode: SCA_FAILED_ERROR_CODE,
                  message: intentResponse.error.message,
                },
              })
            : paymentIntent;
        });
    }
    return Promise.resolve(paymentIntent);
  }

  handleRemoveGiftCard(giftCardNumber: string) {
    // TODO: if you remove the first gift card, we don't update the amount we're using off of the second
    // gift card. Ideally we would redistribute the giftCard onto the second one
    this.props.actions.giftCard.removeGiftCard(giftCardNumber);
  }

  handleSelectCard(card: core.PaymentCard) {
    this.props.actions.selection.selectCard(card);
    this.props.actions.purchase.hidePaymentError();
  }

  refreshCalendarAndGoHome() {
    this.props.actions.lock.clear();
    refreshCalendar(this.props.actions, this.props.calendar?.ticketTypeAccessToken).then(() => {
      this.props.history.push(`/${this.props.match.params.businessDomain}`);
    });
  }

  handleDocumentVisible(event: Event) {
    const document = window.document;
    if (!document.hidden) {
      this.handleUpdatePrice({
        onWindowFocus: true,
      });
    }
  }

  handleChangeGratuity(newGratuityRate: number) {
    this.handleUpdatePrice({
      newGratuityRate,
    });
  }

  // TODO: consolidate this with the similar function in CheckoutRouteEnhanced
  handleUpdatePrice({
    newGratuityRate,
    discountCode,
    onWindowFocus = false,
    failureCount = 0,
  }: {
    newGratuityRate?: number;
    discountCode?: string | null;
    onWindowFocus?: boolean;
    failureCount?: number;
  }) {
    const {
      actions,
      app: { config, patron },
      delivery,
      giftCard,
      selection,
      lock,
      loyaltyProgram,
      purchase,
      basePurchase,
    } = this.props;
    const business = config?.business;
    const effectiveGratuityRate =
      newGratuityRate ?? purchase?.calculatedPrice?.selectedGratuityRate ?? undefined;
    const effectiveDiscountCode = !isUndefined(discountCode)
      ? // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        discountCode!
      : (
          find(
            purchase.calculatedPrice?.discount,
            (discount) => discount.promoCode
          ) as core.Discount
        )?.promoCode;

    const giftCardDiscountsForPurchase = this.buildGiftCardDiscounts();
    return actions.purchase
      .calculatePrice(
        buildPurchaseOrder({
          selection,
          lock,
          ownerPatron: patron ?? undefined,
          dinerPatron: head(
            purchase.calculatedPrice
              ? purchase.calculatedPrice?.dinerPatron
              : basePurchase?.dinerPatron
          ),
          gratuityRate: effectiveGratuityRate,
          promoCode: effectiveDiscountCode,
          giftCardDiscounts: giftCardDiscountsForPurchase,
          deliveryAddress: delivery.deliveryAddress,
          giftCard: giftCard.tockGiftCard,
          compAmountCents:
            basePurchase && basePurchase.compAmountCents ? basePurchase.compAmountCents : undefined,
          customChargeOrder: basePurchase?.purchasedCustomCharge
            ? basePurchase.purchasedCustomCharge
            : undefined,
          purchaseId: basePurchase ? basePurchase.id : undefined,
        }),
        basePurchase ?? undefined,
        basePurchase ? { businessId: basePurchase?.business?.id as number } : undefined
      )
      .then((response) => {
        this.setState({ isInitialPriceCalculated: true });
        if (this.showLoyaltyProgram()) {
          const theme = business?.loyaltyProgramTheme;
          const lastRequestedSubtotal = loyaltyProgram.lastRequestedSubtotal || 0;
          const subtotal = purchase.calculatedPrice?.subtotal || 0;
          if (subtotal !== lastRequestedSubtotal || !loyaltyProgram.balance) {
            return actions.loyaltyProgram.fetchBalance(
              subtotal,
              this.getDiscounts(),
              purchase.calculatedPrice?.ticketCount || 0,
              loyaltyProgram.balance?.uniqueCheckNumber,
              theme
            );
          }
        }
        return response;
      })
      .catch((response) => {
        if (!(response as any)?.error.errorCode) {
          // If we fail with no error code it may be some temporary network connectivity thing
          // (see in dev using Network Conditioner). Retry a couple times before giving up.
          if (failureCount > 1) {
            this.props.actions.toast.showError('Unable to complete your reservation.');
            this.refreshCalendarAndGoHome();
          } else {
            setTimeout(() => {
              this.handleUpdatePrice({
                newGratuityRate,
                discountCode,
                onWindowFocus,
                failureCount: failureCount + 1,
              });
            }, 10);
          }
          return;
        }

        const errorCode = safeToNumber(response.error.errorCode);
        if (errorCode === 3022) {
          this.refreshCalendarAndGoHome();
        } else if (errorCode === 2036) {
          // Delivery is not serviceable, take the user back to entering a delivery address.
          history.back();
        } else if (includes([3000, 3002], errorCode)) {
          if (onWindowFocus) {
            this.props.actions.toast.showError(
              'You locked another reservation elsewhere. This lock has expired.'
            );
          }
          this.refreshCalendarAndGoHome();
        } else if (errorCode === 4037) {
          this.props.actions.purchase.clearDiscountCode();
        }
      });
  }

  handleInitializeGiftCards() {
    const { purchase, actions } = this.props;
    purchase.calculatedPrice?.discount
      .filter((discount) => !!discount.giftCard)
      .forEach((discount) => {
        if (
          !discount.giftCard?.externalIdentifier ||
          !discount.amountCents ||
          !discount.giftCard.giftCardProvider
        ) {
          return;
        }
        actions.giftCard.applyGiftCard({
          number: discount.giftCard?.externalIdentifier,
          pin: discount.giftCard?.externalPin || '',
          amountCents: discount.amountCents,
          provider: discount.giftCard.giftCardProvider,
        });
      });
  }

  handleInitializeAnnexCloudLoyaltyProgram() {
    const { actions } = this.props;
    actions.loyaltyProgram
      .fetchAnnexCloudBalance(this.getSubtotal(), this.getDiscounts())
      .catch((err) => {
        window.LOGGER?.error(err);
      });
  }

  handleSwitchAccount() {
    const {
      actions,
      location,
      history,
      match: { params },
    } = this.props;
    const { currentLock, packageLock } = this.props.lock;
    const { sessionRelockTickets } = this.props.actions.lock;
    let ticketSubsetIds: any[] = [];
    if (currentLock) {
      ticketSubsetIds.push(currentLock.ticketSubset[0].id);
    } else if (packageLock) {
      ticketSubsetIds = map(packageLock.locks as any[], (lock) => lock.ticketSubset.id);
    }

    (ticketSubsetIds.length === 0
      ? Promise.resolve<ProtoResponse>(undefined as any)
      : sessionRelockTickets(ticketSubsetIds)
    ).then(() => {
      actions.selection.clearCard();
      actions.app.logout();
      history.push({
        pathname: `/${params.businessDomain}/login`,
        search: stringify({
          continue: location.pathname,
        }),
        state: {
          defaultTab: 'logIn',
        },
      });
    });
  }

  handleToggleGiftCard(event: React.MouseEvent<HTMLAnchorElement>) {
    event.preventDefault();

    this.setState({
      isGiftCardShown: !this.state.isGiftCardShown,
    });
  }

  scrollToPaymentCard() {
    const scrollOptions = {
      offset: -48,
    };
    const node = ReactDOM.findDOMNode(this.refs.acceptTermsForm) as HTMLElement;
    if (node) {
      scrollToChild(document.body, node, scrollOptions);
      scrollToChild(document.documentElement, node, scrollOptions);
    }
  }

  handleEditLoyaltyProgramLogin() {
    this.props.actions.loyaltyProgram.clearPaytronixLogin();
  }

  handleShowLoyaltyProgramLogin() {
    this.props.actions.ui.openLoyaltyProgramLoginModal();
  }

  async handleSubmitPurchase(formValues: AcceptTermsFormFields): Promise<unknown> {
    const {
      actions,
      app,
      context: { isFeatureEnabled },
      lock: { currentLock },
      paymentCard: { deviceData },
      purchase,
      basePurchase,
      selection,
      newCardPaymentElementData,
      isOwnerSameAsDiner,
    } = this.props;
    const {
      name,
      cvc,
      expiry,
      number,
      optsInToEmail,
      zipCode,
      patron: patronFromForm,
      orderNotes,
      hasVerifiedAge,
      consentsToSMS,
      nonceToken,
      paymentMethod,
      availablePaymentMethods,
      isSupportedApplePayEnvironment,
      storedToken,
    } = formValues;
    const businessConfig = this.getBusinessConfig();
    const card = {
      name,
      cvc,
      expiry,
      number,
      zipCode,
      skipAvsCheck: false,
      nonceToken,
      storedToken,
    };
    const purchaseOrder = purchase.calculatedPrice;

    const isAuthenticated = !!app.jwtToken;
    const usePaidQuickCheckout = this.usesPaidQuickCheckout();
    let patron: core.IPatron = purchaseOrder?.patron ?? {};
    if (!isAuthenticated) {
      patron = patronFromForm;
    }

    const optionalOrderNote = trim(orderNotes);
    const amountDue = (purchaseOrder?.totalPrice ?? 0) - (purchaseOrder?.netAmountPaidCents ?? 0);
    const isCreditCardHold =
      purchaseOrder?.ticketPriceInformation?.priceType === 'PAYMENT_CARD_HOLD';
    const requiresPayment =
      !isCreditCardHold && basePurchase ? amountDue > 0 : this.getRequiresPayment(purchaseOrder);
    const requiresStripe = !!businessConfig?.paymentClientId;
    const options = {
      optsInToEmail,
      hasVerifiedAge,
      optionalOrderNote,
      consentsToSMS,
      paymentMethodDetails: {
        paymentMethod,
        availablePaymentMethods,
        isSupportedApplePayEnvironment,
        isSavedCreditCard: !!selection.card,
        paymentProcessorType: (requiresStripe
          ? 'STRIPE'
          : 'BRAINTREE') as core.PaymentProcessorType,
      },
    };

    // Ensure that, if purchase is a DoorDash order, we're not violating the DoorDash gratuity limit
    if (
      purchaseOrder &&
      isGratuityViolatingDoordashLimit(
        purchaseOrder.gratuityCents,
        currentLock?.ticketType?.[0]?.deliveryServiceProvider ?? undefined
      )
    ) {
      actions.toast.showError(doorDashMaxTipExceededErrorString);
      return Promise.reject(null);
    }

    if (paymentMethod === 'APPLE_PAY') {
      if (!hasRequiredApplePayFields(businessConfig?.paymentProcessorType, formValues)) {
        return Promise.reject(TRY_ANOTHER_METHOD_ERROR_MESSAGE);
      }

      return this.handleStartPurchase(card, patron, options);
    }

    const skipTokenization =
      !!(
        selection.card &&
        !window.braintreeHostedField &&
        (selection.card.storedToken || selection.card.chasePaySession)
      ) || !requiresPayment;

    if (skipTokenization) {
      if (requiresPayment && requiresStripe && !window.stripeV3) {
        this.props.actions.toast.showError('Tock could not contact payment processing services.');
        return Promise.reject(null);
      }
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const paymentCard = selection.card!;
      return this.handleStartPurchase(paymentCard, patron, options);
    } else if (!(window as any).Stripe) {
      this.props.actions.toast.showError('Tock could not contact payment processing services.');
      return Promise.reject(null);
    }

    const saveCardToTockOptions: saveCardToTockOptions = {
      scope: this.getBusinessScope(),
      createWithoutPatron:
        (isCreditCardHold && !this.props.isOwnerSameAsDiner) || usePaidQuickCheckout,
      ...(usePaidQuickCheckout && { patron: patron }),
    };

    if (!window.braintreeHostedField) {
      const tokenizedCard = await newCardPaymentElementData.tokenizePaymentCard({ name });
      const card = { ...tokenizedCard, deviceData };

      let paymentCard;

      if (this.getShouldSaveCreditCard() || isCreditCardHold) {
        if (usePaidQuickCheckout) {
          saveCardToTockOptions.challengeResponse = this.state.turnstileResponse;
          saveCardToTockOptions.requireTurnstile = isFeatureEnabled('TURNSTILE_RENDER_CLIENT');
        }
        try {
          const { payload } = await actions.paymentCard.saveCardToTock(card, saveCardToTockOptions);
          paymentCard = payload;
        } catch (_error) {
          newCardPaymentElementData.reset();
          this.turnstileValidatorRef.current?.refreshToken();
          return;
        }
      } else if (
        !isOwnerSameAsDiner &&
        isBraintreeBusiness(app.config?.business as consumer.BusinessConfig)
      ) {
        // For a concierge booking at a BT business, we need to convert the Stripe card to a BT card
        try {
          const { payload } = await actions.paymentCard.forwardCardWithoutSaving(card);
          paymentCard = payload;
        } catch (_error) {
          newCardPaymentElementData.reset();
          return;
        }
      } else {
        paymentCard = card;
      }

      try {
        return this.handleStartPurchase(paymentCard, patron, options);
      } catch (_error) {
        actions.selection.selectCard(paymentCard);
      }
    }

    let promise = asyncValidateCreditCardForm(formValues);
    if (window.braintreeHostedField) {
      promise = promise
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        .then(() => addCvvToken(selection.card!))
        .then((card: core.IPaymentCard) => this.handleStartPurchase(card, patron, options));
    } else if (this.getShouldSaveCreditCard() || isCreditCardHold) {
      const newCard = {
        ...card,
        deviceData,
        skipAvsCheck: false,
      };

      const tokenizeAndSaveMethod = isFeatureEnabled('STRIPE_FORWARD_API')
        ? actions.paymentCard.tokenizeAndSaveCardWithStripeForwarding
        : actions.paymentCard.tokenizeAndSaveCard;

      promise = promise
        .then(() => tokenizeAndSaveMethod(newCard, saveCardToTockOptions))
        .then((response: any) => this.handleStartPurchase(response.payload, patron, options));
    } else {
      promise = promise
        .then(() => actions.paymentCard.tokenizeCard(card))
        .then((card: any) => this.handleStartPurchase(card, patron, options));
    }
    return promise;
  }

  getRequiresPayment(purchaseOrder?: core.PurchaseOrder) {
    if (purchaseOrder) {
      return getAmountDue(purchaseOrder) > 0 || getHoldAmountDue(purchaseOrder) > 0;
    }
    return false;
  }

  handleApplyDiscountCode(discountCode: string) {
    const { calculatedPrice } = this.props.purchase;
    if (!calculatedPrice) {
      throw new Error('Price not calculated for discount code to be applied');
    }
    return this.props.actions.purchase
      .validateDiscountCode(calculatedPrice.ticketTypeId, discountCode)
      .then(() => {
        this.handleUpdatePrice({
          discountCode,
        });
      })
      .catch(() => {
        this.props.actions.purchase.clearDiscountCode();
        this.props.actions.toast.showError(
          'Unable to apply discount code, it is invalid or may have expired'
        );
      });
  }

  handleRemoveDiscountCode() {
    this.props.actions.purchase.clearDiscountCode();
    this.handleUpdatePrice({
      discountCode: null,
    });
  }

  handleLoyaltyProgramLogin(values: LoyaltyProgramLoginFormFields) {
    const {
      loyaltyProgram,
      lock: { currentLock },
      purchase,
    } = this.props;
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const ticketType = currentLock!.ticketType[0];
    const redemptionDisabled =
      purchase?.calculatedPrice?.subtotal ||
      0 === 0 ||
      (ticketType?.disableLoyaltyProgramRedemption ?? true);

    return this.props.actions.loyaltyProgram
      .login(values.email, values.password)
      .then((response: any) => {
        if (response.payload) {
          this.props.actions.loyaltyProgram
            .fetchBalance(
              purchase?.calculatedPrice?.subtotal || 0,
              this.getDiscounts(),
              this.props.purchase?.calculatedPrice?.ticketCount || 0,
              loyaltyProgram?.uniqueCheckNumber
            )
            .then(({ payload }) => {
              if (redemptionDisabled || payload.numPointsToRedeem === 0) {
                this.props.actions.ui.closeLoyaltyProgramLoginModal();
              }
            });
        } else {
          this.props.actions.toast.showError('Invalid email or password.');
        }
      });
  }

  handleLoyaltyProgramRedemption(values: LoyaltyProgramRedemptionFormFields) {
    this.props.actions.loyaltyProgram.changeNumPoints(toNumber(values.points));
    this.props.actions.ui.closeLoyaltyProgramLoginModal();
    setTimeout(() => this.handleUpdatePrice({}));
  }

  getBusinessConfig() {
    const { app, business } = this.props;
    return app.config?.business ? app.config.business : business;
  }

  getBusinessScope(): ApiScope | undefined {
    const businessConfig = this.getBusinessConfig();
    if (businessConfig && businessConfig.id) {
      return { businessId: businessConfig.id };
    }
    return undefined;
  }

  shouldAskOptInQuestion(): boolean {
    const { basePurchase, isOwnerSameAsDiner, app } = this.props;
    return !basePurchase && isOwnerSameAsDiner && !!app.config?.business?.requestEmailOptIn;
  }

  getIsBookViaEmail(lock: core.TicketGroup): boolean {
    if (lock && lock.ticketSubset && !isNil(first(lock.ticketSubset))) {
      const purchaseVerifyInfo = lock.ticketSubset[0].purchaseVerifyInfo;
      return !isNil(purchaseVerifyInfo) && !isEmpty(purchaseVerifyInfo);
    }
    return false;
  }

  getIsTransferRoute(): boolean {
    return includes(this.props.match.url, 'checkout/transfer');
  }

  getRequiredAge(): number | undefined {
    const { selectedExperience, purchase } = this.props;
    return getRequiredAge(
      selectedExperience,
      (purchase.calculatedPrice?.optionOrder as core.OptionOrder[]) ?? []
    );
  }

  showLoyaltyProgram(): boolean {
    const {
      app,
      context,
      lock: { currentLock },
    } = this.props;
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const ticketType = currentLock!.ticketType[0];
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const selectedDateTime = LocalDateTime.parse(currentLock!.dateTime);
    const theme = app?.config?.business?.loyaltyProgramTheme;
    return (
      !ticketType.disableLoyaltyProgram &&
      context.isFeatureEnabled('LEYE_LOYALTY_PROGRAM') &&
      // LEYE has a hard rule about not allowing point accrual or redemption after 3pm on Saturdays only.
      (!(
        safeEnumCompare(theme, LoyaltyProgramConsumerTheme, LoyaltyProgramConsumerTheme.LEYE) &&
        selectedDateTime.dayOfWeek() === DayOfWeek.SATURDAY &&
        selectedDateTime.hour() >= 15
      ) ||
        safeEnumCompare(
          theme,
          LoyaltyProgramConsumerTheme,
          LoyaltyProgramConsumerTheme.ANNEX_CLOUD_DEFAULT
        ))
    );
  }

  showConciergeTOS() {
    const { purchase } = this.props;
    const purchaseOrder = purchase.calculatedPrice;
    if (purchaseOrder?.visitEditToken) {
      return false;
    }
    const isPatronAPartner = !!this.props.isPatronAPartner;
    return !this.props.isOwnerSameAsDiner && isPatronAPartner;
  }

  handleChangeUseBalance(event: React.ChangeEvent<HTMLInputElement>, checked: boolean) {
    this.updateGiftCardState(checked);
  }

  updateGiftCardState(checked: boolean) {
    const {
      giftCardBalance: { data: giftCard },
      actions,
    } = this.props;
    if (giftCard) {
      this.setState((prevState: State) => ({
        useGiftCardBalance: checked,
      }));

      if (checked) {
        actions.giftCard.addTockGiftCard({
          externalIdentifier: giftCard.cardNumber,
          amountCents: giftCard.balanceCents,
        });
      } else {
        actions.giftCard.removeTockGiftCard();
      }
    }
  }

  render() {
    const {
      actions,
      app,
      context: { isFeatureEnabled },
      business: propBusiness,
      delivery: { deliveryAddress },
      giftCard,
      giftCardBalance,
      isInQuickCheckout,
      lastGuest,
      lock,
      loyaltyProgram,
      purchase,
      ui,
      selectedExperience,
      selection,
      basePurchase,
      doubleBooking,
      newCardPaymentElementData,
      stripePublicKey,
      shouldAskForSmsConsent,
    } = this.props;
    let business = app.config?.business;
    if (!business && basePurchase) {
      business = propBusiness;
    }
    const { isGiftCardShown, isInitialPriceCalculated, useGiftCardBalance } = this.state;

    if (!isInitialPriceCalculated) {
      return null;
    }

    const purchaseOrder = purchase.calculatedPrice;
    const { currentLock } = lock;
    if (!purchaseOrder || !currentLock) {
      return null;
    }

    const ticketType = currentLock.ticketType[0] as core.TicketType;
    const showDiscountCodeForm = !!ticketType?.hasPromoCode;
    const ticketTypeDiscountCode = purchase.ticketTypeDiscountCode;

    const diner = getDiner(purchaseOrder);
    const owner = app.patron;
    const canSwitchAccount =
      !this.getIsBookViaEmail(currentLock) && !this.getIsTransferRoute() && !basePurchase;

    const switchAccountFunction = canSwitchAccount ? this.handleSwitchAccount : undefined;

    // Only enable gift cards for non-package checkout flows.
    const totalPrice = this.getTotalPrice();
    const appliedGiftCards: GiftCard[] = filter(
      map(purchaseOrder?.discount, (discount) => {
        if (!discount.giftCard) {
          return null;
        }

        return {
          number: discount.giftCard.externalIdentifier,
          amountCents: discount.amountCents,
        };
      })
    ) as GiftCard[];
    const extraChargeDetails = ticketType.extraChargeDetails;
    const dateTime = LocalDateTime.parse(purchaseOrder.dateTime);
    const giftCardProvider = business?.giftCardProvider;
    const giftCardPinType = business?.giftCardPinType;
    const showGiftCards =
      this.props.context.isFeatureEnabled('GIFT_CARDS') &&
      totalPrice > 0 &&
      (safeEnumCompare(
        giftCardProvider,
        Business.GiftCardProvider,
        Business.GiftCardProvider.TCC
      ) ||
        safeEnumCompare(
          giftCardProvider,
          Business.GiftCardProvider,
          Business.GiftCardProvider.PAYTRONIX
        ));

    const isAuthenticated = !!app.jwtToken;
    const requiresPayment = this.getRequiresPayment(purchase.calculatedPrice);
    const showLoyaltyProgram = isAuthenticated && this.showLoyaltyProgram();
    const loadPaymentScripts = requiresPayment;
    const isTablesideOrder = !!purchaseOrder.visitEditToken || !!selection.visitEditPaymentMode;
    const loadKount = loadPaymentScripts && !isTablesideOrder;

    const hasRefund =
      (purchase?.calculatedPrice?.netAmountPaidCents ?? 0) >
      (purchase?.calculatedPrice?.totalPrice ?? 0);

    const hasGiftCardMoney = giftCardBalance.data && giftCardBalance.data.balanceCents > 0;

    const showTockGiftCard =
      !isFeatureEnabled('GOODBYE_TOCK_GIFT_CARDS') &&
      purchaseOrder.isTockGiftCardEligible &&
      hasAnyMoniesInvolved(purchaseOrder) &&
      !hasRefund &&
      hasGiftCardMoney &&
      this.props.isOwnerSameAsDiner &&
      totalPrice - (purchase?.calculatedPrice?.netAmountPaidCents ?? 0) > 0;

    const businessConfig = business as consumer.BusinessConfig;
    const defaultOptInText = isFeatureEnabled('CONSUMER_CASL_VENUE_OPT_IN')
      ? DEFAULT_OPT_IN_TEXT_CASL
      : DEFAULT_OPT_IN_TEXT;

    return (
      <PaymentScriptLoader
        loadBraintree={loadPaymentScripts}
        loadStripe={loadPaymentScripts}
        stripePublicKey={stripePublicKey}
        loadKount={loadKount}
        deviceData={this.props.paymentCard.deviceData}
      >
        <Section>
          <Head title={`${business?.name} - Confirm`} />
          <div className="Consumer-smallContentContainer ConsumerCheckout--fadeIn">
            <>
              <div
                css={({ spacing }: Theme) => css`
                  margin-bottom: ${spacing(2)}px;
                `}
              >
                <TicketTypeVarietySpecificInstructions
                  ticketTypeVariety={ticketType.variety || 'FULL'}
                  deliveryServiceProvider={ticketType.deliveryServiceProvider}
                  reserveShippingTime={ticketType.reserveShippingTime}
                  businessName={business?.name || 'the restaurant'}
                />
              </div>
              {doubleBooking ? (
                <DoubleBookingWarning
                  doubleBooking={doubleBooking}
                  currentRestaurantName={business?.name ?? undefined}
                />
              ) : null}

              <PurchaseOrderSummary
                alwaysShowAmountDue={!!basePurchase}
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                business={business!}
                dateTime={dateTime}
                deliveryAddress={deliveryAddress}
                diner={diner}
                extraChargeDetails={extraChargeDetails as core.ExtraChargeDetails}
                isAuthenticated={isAuthenticated}
                isEvent={isLockOfEvent(currentLock)}
                isLastExperience={true}
                onSwitchAccounts={switchAccountFunction}
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                owner={owner!}
                purchase={purchaseOrder}
                totalPrice={totalPrice}
                onGratuityChange={this.handleChangeGratuity}
                showTockFeeIfRequired={true}
                ticketType={ticketType}
                isTableSideOrderPayment={selection.visitEditPaymentMode}
              />

              {showTockGiftCard && giftCardBalance.data ? (
                <GiftCardBalanceWrapper
                  amountCents={giftCardBalance.data.balanceCents}
                  businessSupportsGiftCards={isBraintreeBusiness(businessConfig)}
                  onUseBalanceChange={this.handleChangeUseBalance}
                  useGiftCardBalance={useGiftCardBalance}
                />
              ) : null}

              {(showDiscountCodeForm || showLoyaltyProgram) && !purchaseOrder.basePurchaseId && (
                <RewardsAndDiscountsForm
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  business={business!}
                  loyaltyProgram={loyaltyProgram}
                  onApplyDiscountCode={this.handleApplyDiscountCode}
                  onEditLoyaltyProgramLogin={this.handleEditLoyaltyProgramLogin}
                  onEditLoyaltyProgramRedemption={this.handleShowLoyaltyProgramLogin}
                  onShowLoyaltyProgramLogin={this.handleShowLoyaltyProgramLogin}
                  onRemoveDiscountCode={this.handleRemoveDiscountCode}
                  purchaseOrder={purchaseOrder}
                  showLoyaltyProgram={showLoyaltyProgram}
                  showDiscountCodeForm={showDiscountCodeForm}
                  ticketType={ticketType}
                  ticketTypeDiscountCode={ticketTypeDiscountCode}
                />
              )}

              {showGiftCards && (
                <div className="ConsumerCheckout-section">
                  {isGiftCardShown ? (
                    <GiftCardWrapper
                      totalPrice={totalPrice}
                      giftCardProvider={giftCardProvider || 'TCC'}
                      giftCardPinType={giftCardPinType || 'NONE'}
                      giftCards={appliedGiftCards}
                      giftCardRequestInProgress={giftCard.giftCardRequestInProgress}
                      onRemoveGiftCard={this.handleRemoveGiftCard}
                      onAddGiftCard={this.handleAddGiftCard}
                    />
                  ) : (
                    <Anchor
                      className="ConsumerCheckout-addGiftCard"
                      href="#"
                      onClick={this.handleToggleGiftCard}
                    >
                      <Typography color="inherit" component="span" variant="body02">
                        Redeem a gift card
                      </Typography>
                    </Anchor>
                  )}
                </div>
              )}

              <AcceptTermsForm
                business={this.getBusinessConfig()}
                communicationsOptInSettings={{
                  showControl: this.shouldAskOptInQuestion(),
                  isInitiallyOptedIn: app.restaurantCommunicationsOptIn,
                  optInText: app.config?.business?.emailOptInText ?? defaultOptInText,
                }}
                communicationPolicy={
                  this.props.selectedExperience?.communicationPolicy ?? undefined
                }
                hasExternalGiftCardPayment={purchaseOrder.discount.some((disc) => disc.giftCard)}
                hasLoyaltyPointsPayment={purchaseOrder.discount.some((disc) => disc.loyaltyProgram)}
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                detectedLocation={app.detectedLocation!}
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                guest={diner || owner!}
                showConciergeTOS={this.showConciergeTOS()}
                isConcierge={this.showConciergeTOS()}
                isAuthenticated={isAuthenticated}
                isFreeBooking={this.getIsTotallyFreeReservation()}
                onTurnstileUpdate={this.handleTurnstileResponse}
                isConciergeBookViaEmail={false}
                isInQuickCheckout={isInQuickCheckout ?? false}
                lastGuest={lastGuest}
                newCardPaymentElementData={newCardPaymentElementData}
                onSelectCard={this.handleSelectCard}
                onPurchase={this.handleSubmitPurchase}
                purchase={purchase}
                basePurchase={basePurchase}
                // ref="acceptTermsForm"
                shouldSaveCreditCard={this.getShouldSaveCreditCard()}
                totalPrice={totalPrice}
                selectedExperience={selectedExperience}
                requiredAge={this.getRequiredAge()}
                isTableSideOrder={!!purchaseOrder.visitEditToken}
                isTableSideOrderPayment={
                  !!purchaseOrder.visitEditToken && !!selection.visitEditPaymentMode
                }
                isCreditCardHold={
                  purchaseOrder.ticketPriceInformation?.priceType === 'PAYMENT_CARD_HOLD'
                }
                applePayProcessorType={convertToApplePayProcessingType(
                  businessConfig.paymentProcessorType
                )}
                doubleBooking={doubleBooking}
                countryCode={business?.country ?? undefined}
                currencyCode={business?.currencyCode ?? undefined}
                {...getPaymentDetails(purchase, false)}
                turnstileValidatorRef={this.turnstileValidatorRef}
                lock={lock}
                shouldAskForSmsConsent={shouldAskForSmsConsent}
              />
            </>
          </div>
        </Section>
        {business && (
          <LoyaltyProgramModal
            business={business}
            isOpen={ui.showLoyaltyProgramLoginModal}
            loyaltyProgram={loyaltyProgram}
            onClose={actions.ui.closeLoyaltyProgramLoginModal}
            onLogin={this.handleLoyaltyProgramLogin}
            onFinish={this.handleLoyaltyProgramRedemption}
          />
        )}
      </PaymentScriptLoader>
    );
  }
}

export default withNewCardPaymentElement(CompletePurchase);
